import { OTHER_LCA_SOFTWARE_NAME, YES_NO } from 'components/v2/compiler/constants';
import ContributionOfScrapInputsGridPdf from 'components/v2/compiler/grids/contribution-of-scrap-inputs/ContributionOfScrapInputsGridPdf';
import DataQualityAssessmentAndReferenceYearsPdf from 'components/v2/compiler/grids/data-quality-assessment-and-ref-years/DataQualityAssessmentAndReferenceYearsPdf';
import DeclarationOfCO2GridPdf from 'components/v2/compiler/grids/declaration-of-co2/DeclarationOfCo2GridPdf';
import TransportationPerformanceGridPdf from 'components/v2/compiler/grids/specification/TransportationPerformanceGridPdf';
import { NOT_ASSIGNED } from 'constants/constants';
import React from 'react';
import { EPD_DICTIONARY_CODES } from 'services/api/constants';
import {
  DataSourceOptionModel,
  DictionaryValue,
  EPDBasedOnUnitEnum,
  EPDDataSourceModel,
  Option,
  ScrapInputsContributionLevelEnum,
} from 'types/types';
import DebugOnly from 'util/dev-tools/DebugOnly';
import RenderCounter from 'util/dev-tools/RenderCounter';
import { PdfAreaDontBreak, PdfGridsArea, PdfH1, PdfHtmlContent, PdfPage, PdfTable, PdfThLabel } from './Styles.styled';
import ElectricityListPdf from 'components/v2/compiler/grids/electricity/ElectricityListPdf';
import BiogasDataListPdf from 'components/v2/compiler/grids/biogas/BiogasDataListPdf';
import usePcrV2 from 'components/v2/compiler/hooks/usePcrV2';
import CheckboxesCellPdf from './components/CheckboxesCellPdf';
import CheckboxGroupPdf from './components/CheckboxGroupPdf';
import usePdfData from './utils/usePdfData';
import { getOptionsFromDictionary } from 'util/utils';
import { isNullOrEmptyHtml } from 'pages/wizard/Validation';

const LCAInformationPage: React.FC = () => {
  const {
    epdVersionId,
    epdDefinition,
    lcaSpecification,
    infrastructureAndCapitalGoods,
    dictionaryValues,
    dataSources,
    dataSourceOptions: dataSourceOptionsSource,
    referenceFlow: referenceFlowData,
    conversionFactor: conversionFactorData,
    cO2UptakeAssociation,
    lcaSoftwareOptions,
    referencePackageVersions15804,
    flowPropertiesLookup,
  } = usePdfData();
  const epdBasedOnUnitOptions = getOptionsFromDictionary(dictionaryValues, EPD_DICTIONARY_CODES.EPD_BASED_ON_UNIT);
  const scrapInputsContributionLevelOptions = getOptionsFromDictionary(
    dictionaryValues,
    EPD_DICTIONARY_CODES.SCRAP_INPUT_CONTRIBUTION_LEVEL
  );
  const isPcrV2 = usePcrV2();

  const getDataSourceVersionOptions = (dataSourceId?: string) => {
    if (!dataSourceId) {
      return [] as Option[];
    }

    const dataSource = dataSourceOptionsSource?.find(
      (x: DataSourceOptionModel) => x.id.toLowerCase() === dataSourceId.toLowerCase()
    );

    return dataSource?.dataSourceVersions;
  };

  const selectedEPDBasedOnUnit = epdBasedOnUnitOptions.find(
    (x: any) =>
      lcaSpecification?.epdBasedOnUnit &&
      x.value === Object.keys(EPDBasedOnUnitEnum).indexOf(lcaSpecification?.epdBasedOnUnit).toString()
  );

  const selectedLCASoftwareOption = lcaSoftwareOptions?.find(
    (x) => lcaSpecification?.lcaSoftwareId && x.id.toString() === lcaSpecification?.lcaSoftwareId.toString()
  );

  const getSelectedLCASoftwareData = (lcaSoftware: any, lcaSoftwareVersionId: string | undefined) => {
    if (!lcaSoftware) {
      return null;
    }

    let softwareName;
    let softwareVersion;
    if (lcaSoftware?.name === OTHER_LCA_SOFTWARE_NAME) {
      softwareName = lcaSpecification?.otherSoftwareName;
      softwareVersion = lcaSpecification?.otherSoftwareVersion;
    } else {
      softwareName = lcaSoftware?.name;
      softwareVersion = lcaSoftware?.lcaSoftwareVersions?.find(
        (x: Option) => x.value.toLowerCase() === lcaSoftwareVersionId?.toLowerCase()
      )?.label;
    }

    return (
      softwareName || softwareVersion ? `${softwareName ? softwareName + ' ' : ''}${softwareVersion ?? ''}` : null
    )?.trim();
  };

  const selectedScrapInputsContributionLevel = scrapInputsContributionLevelOptions.find(
    (x: any) =>
      lcaSpecification?.scrapInputsContributionLevel &&
      x.value ===
        Object.keys(ScrapInputsContributionLevelEnum).indexOf(lcaSpecification?.scrapInputsContributionLevel).toString()
  );

  const selectedReferenceFlowUnit = flowPropertiesLookup?.find(
    (x) => x.id.toLowerCase() === referenceFlowData?.unitId?.toLowerCase()
  );

  const referenceFlowName =
    lcaSpecification?.epdBasedOnUnit === EPDBasedOnUnitEnum.DeclaredUnit
      ? 'Declared unit and reference flow'
      : 'Reference flow';

  const isTransportationTableEnabled = lcaSpecification?.shouldTransportationPerformanceBeFetched ?? false;
  const isCO2Visible = lcaSpecification?.shouldDeclarationsBeFetched ?? false;

  return (
    <PdfPage>
      <DebugOnly>
        <RenderCounter componentName="LCA information" threshold={3} />
      </DebugOnly>

      <PdfH1>LCA information</PdfH1>
      <PdfGridsArea>
        <PdfTable>
          <thead>
            <tr>
              <PdfThLabel />
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>EPD based on declared or functional unit</td>
              <td>{selectedEPDBasedOnUnit?.label}</td>
            </tr>
            {lcaSpecification?.epdBasedOnUnit === EPDBasedOnUnitEnum.FunctionalUnit && (
              <tr>
                <td>Functional unit description</td>
                <td>{lcaSpecification?.functionalUnitDescription}</td>
              </tr>
            )}
            <tr>
              <td>{referenceFlowName}</td>
              <td>
                {referenceFlowData?.productFlowName && (
                  <>
                    {referenceFlowData.productFlowName}
                    <br />
                  </>
                )}
                {(selectedReferenceFlowUnit || referenceFlowData?.value) && (
                  <span
                    dangerouslySetInnerHTML={{
                      __html: `${selectedReferenceFlowUnit?.unitName ?? ''}: ${referenceFlowData?.value ?? ''} ${
                        selectedReferenceFlowUnit?.unit ?? ''
                      }`,
                    }}
                  />
                )}
              </td>
            </tr>
            <tr>
              <td>Conversion factor to mass</td>
              <td>{conversionFactorData?.value}</td>
            </tr>
            <tr>
              <td>Are infrastructure or capital goods included in any upstream, core or downstream processes?</td>
              <td>
                <CheckboxesCellPdf>
                  <CheckboxGroupPdf
                    label="Yes"
                    checked={infrastructureAndCapitalGoods?.includedInProcesses === YES_NO.YES}
                  />
                  <CheckboxGroupPdf label="No" checked={infrastructureAndCapitalGoods?.includedInProcesses === YES_NO.NO} />
                </CheckboxesCellPdf>
              </td>
            </tr>
            {isPcrV2 && infrastructureAndCapitalGoods?.includedInProcesses === YES_NO.YES && (
              <tr>
                <td>
                  Do infrastructure and capital goods contribute more than 10% to the A1-A3 (A1-A5 for services) results of
                  any environmental impact indicator declared in the EPD?
                </td>
                <td>
                  <CheckboxesCellPdf>
                    <CheckboxGroupPdf label="Yes" checked={infrastructureAndCapitalGoods.contributionLevel === YES_NO.YES} />
                    <CheckboxGroupPdf label="No" checked={infrastructureAndCapitalGoods.contributionLevel === YES_NO.NO} />
                  </CheckboxesCellPdf>
                </td>
              </tr>
            )}
            {infrastructureAndCapitalGoods?.includedInProcesses === YES_NO.YES &&
              (!isPcrV2 || infrastructureAndCapitalGoods.contributionLevel === YES_NO.YES) && (
                <tr>
                  <td>
                    {isPcrV2
                      ? 'Description of infrastructure and capital goods'
                      : 'Detailed description of infrastructure and capital goods'}
                  </td>
                  <td>
                    <PdfHtmlContent
                      dangerouslySetInnerHTML={{
                        __html: (infrastructureAndCapitalGoods?.description ?? '').replace(/\n/g, ''),
                      }}
                    />
                  </td>
                </tr>
              )}
            <tr>
              <td>Data sources used for this EPD</td>
              <td>
                {dataSources?.map((item: EPDDataSourceModel, index: number) => (
                  <React.Fragment key={index}>
                    {
                      dataSourceOptionsSource?.find(
                        (x: DataSourceOptionModel) => x.id.toLowerCase() === item.dataSourceId?.toLowerCase()
                      )?.name
                    }
                    <br />
                    {
                      getDataSourceVersionOptions(item.dataSourceId)?.find(
                        (x: Option) => x.value.toLowerCase() === item?.dataSourceVersionId?.toLowerCase()
                      )?.label
                    }
                    {index < dataSources.length - 1 && (
                      <>
                        <br />
                        <br />
                      </>
                    )}
                  </React.Fragment>
                ))}

                {dataSources?.length > 0 && epdDefinition?.isOtherDatabaseUsedAsDatasource && (
                  <>
                    <br />
                    <br />
                  </>
                )}

                {epdDefinition?.isOtherDatabaseUsedAsDatasource && (
                  <>
                    Other database
                    <br />
                    {epdDefinition.otherDatabaseUsedAsDatasourceDescription}
                  </>
                )}
              </td>
            </tr>
            <tr>
              <td>LCA Software</td>
              <td>{getSelectedLCASoftwareData(selectedLCASoftwareOption, lcaSpecification?.lcaSoftwareVersionId)}</td>
            </tr>
            {!isNullOrEmptyHtml(lcaSpecification?.lcaBasedDescription) && (
              <tr>
                <td>Additional information about the underlying LCA-based information</td>
                <td>
                  <PdfHtmlContent
                    dangerouslySetInnerHTML={{
                      __html: (lcaSpecification?.lcaBasedDescription ?? '').replace(/\n/g, ''),
                    }}
                  />
                </td>
              </tr>
            )}
            <tr>
              <td>Version of the EN 15804 reference package</td>
              <td>
                {
                  (
                    referencePackageVersions15804?.versions.find(
                      (x) => x.id == epdDefinition?.referencePackageVersion15804
                    ) ?? { name: '' }
                  ).name
                }
              </td>
            </tr>
            {(isPcrV2 || epdDefinition?.characterisationMethods) && (
              <tr>
                <td>Characterisation methods</td>
                <td>{epdDefinition?.characterisationMethods}</td>
              </tr>
            )}
            <tr>
              <td>Technology description including background system</td>
              <td>{lcaSpecification?.technologyDescription}</td>
            </tr>
            <tr>
              <td>Scrap (recycled material) inputs contribution level</td>
              <td>{selectedScrapInputsContributionLevel?.label}</td>
            </tr>
          </tbody>
        </PdfTable>

        <ContributionOfScrapInputsGridPdf epdVersionId={epdVersionId} />

        <DataQualityAssessmentAndReferenceYearsPdf epdVersionId={epdVersionId} />

        <ElectricityListPdf epdVersionId={epdVersionId} />

        <BiogasDataListPdf epdVersionId={epdVersionId} />
        {(isTransportationTableEnabled || isCO2Visible) && (
          <>
            {isTransportationTableEnabled && (
              <PdfAreaDontBreak>
                <TransportationPerformanceGridPdf epdVersionId={epdVersionId} />
              </PdfAreaDontBreak>
            )}
            {isCO2Visible && (
              <PdfAreaDontBreak>
                <DeclarationOfCO2GridPdf
                  epdVersionId={epdVersionId}
                  disabled={cO2UptakeAssociation?.association !== YES_NO.YES}
                />
              </PdfAreaDontBreak>
            )}
          </>
        )}
      </PdfGridsArea>
    </PdfPage>
  );
};

export default LCAInformationPage;
